import { StyleSheet, css } from 'aphrodite';
import { subDays } from 'date-fns';
import { useMemo, useState } from 'react';
import { toast } from 'sonner';
import { useUserSession } from '../../../models/hooks/appStateHooks';
import { Button } from '../../../web/components-v2/Button';
import { DateInput } from '../../../web/components-v2/DateInput';
import {
	Drawer,
	DrawerContent,
	DrawerDescription,
	DrawerFooter,
	DrawerHeader,
	DrawerTitle,
} from '../../../web/components-v2/Drawer';
import { Field, FieldLabel } from '../../../web/components-v2/Field';
import { Skeleton } from '../../../web/components-v2/Skeleton';
import { SPACE, THEME } from '../../../web/components-v2/Theme/theme';
import { apiErrorMessage } from '../../focused-view/data/apiErrorMessage';
import {
	ITimeEntry,
	ITimeEntryFilter,
	useOpenBlockedDayMutation,
	useReviewTimeEntryMutation,
	useTimeEntryFilterQuery,
} from '../../queries/TimeEntry';
import { LoadError } from '../../timeTracking/components/LoadError';
import { TimeEntryHistory } from '../../timeTracking/components/TimeEntryHistory';
import { TimeEntryStatusPill } from '../../timeTracking/components/TimeEntryStatusPill';
import { formatDayShort, isYmd, todayYmd } from '../../timeTracking/days';
import { formatHours } from '../../timeTracking/hours';
import { timeTrackingStyles as tt } from '../../timeTracking/styles';
import { ReasonDialog } from './ReasonDialog';

const RECENT_DAYS = 30;

export function RepDaysDrawer({
	userId,
	isAdmin,
	canActAsSelf,
	nameFor,
	onClose,
}: {
	userId: string | null;
	isAdmin: boolean;
	canActAsSelf: boolean;
	nameFor: (userId?: string) => string;
	onClose: () => void;
}) {
	const [selected, setSelected] = useState<ITimeEntry | null>(null);
	const [reopening, setReopening] = useState<ITimeEntry | null>(null);
	const [openingDay, setOpeningDay] = useState(false);
	const [dayToOpen, setDayToOpen] = useState('');

	const filter = useMemo<ITimeEntryFilter>(
		() => ({
			criteria: [
				{ property: 'User', value: userId ?? undefined },
				{ op: 'Gt', property: 'Day', value: todayYmd(subDays(new Date(), RECENT_DAYS)) },
			],
		}),
		[userId]
	);
	const entriesQuery = useTimeEntryFilterQuery({ enabled: !!userId, filter });
	const entries = entriesQuery.data?.values ?? [];
	const userSession = useUserSession();
	const canUseAdminActions = isAdmin && canActAsSelf && userId !== userSession.user?.id;

	const reopen = useReviewTimeEntryMutation({
		onError: err => toast.error(apiErrorMessage(err, 'Failed to reopen the day')),
		onSuccess: () => {
			toast.success('Day reopened. The rep gets an email with your comment.');
			setReopening(null);
		},
	});
	const openBlocked = useOpenBlockedDayMutation({
		onError: err => toast.error(apiErrorMessage(err, 'Failed to open the day')),
		onSuccess: () => {
			toast.success('Day opened. The rep can now enter hours for it.');
			setOpeningDay(false);
			setDayToOpen('');
		},
	});

	const repName = nameFor(userId ?? undefined);
	const latest = entries.reduce<ITimeEntry | undefined>(
		(current, entry) => (!current || entry.day > current.day ? entry : current),
		undefined
	);

	return (
		<>
			<Drawer open={!!userId} onOpenChange={open => !open && onClose()} swipeDirection='right'>
				<DrawerContent>
					<DrawerHeader>
						<DrawerTitle>{repName}</DrawerTitle>
						<DrawerDescription>
							{[
								latest?.role,
								latest?.officeLocation,
								latest?.managerUserId ? `reports to ${nameFor(latest.managerUserId)}` : null,
							]
								.filter(Boolean)
								.join(' · ') || `Last ${RECENT_DAYS} days`}
						</DrawerDescription>
					</DrawerHeader>
					<div className={css(styles.body)}>
						{entriesQuery.isLoading ? (
							<Skeleton styles={[styles.skeleton]} />
						) : entriesQuery.isError ? (
							<LoadError message='We couldn’t load this rep’s hours.' onRetry={() => entriesQuery.refetch()} />
						) : entries.length ? (
							<ul className={css(styles.days)}>
								{entries.map(entry => (
									<li key={entry.id} className={css(styles.day, selected?.id === entry.id && styles.daySelected)}>
										<Button
											type='button'
											variant='ghost'
											styles={[styles.dayButton]}
											aria-pressed={selected?.id === entry.id}
											onClick={() => setSelected(selected?.id === entry.id ? null : entry)}
										>
											<span className={css(styles.dayTitle)}>
												{formatDayShort(entry.day)} · <span className={css(tt.number)}>{formatHours(entry.hours)}</span>
											</span>
											<TimeEntryStatusPill entry={entry} />
										</Button>
										{canUseAdminActions && entry.status === 'Approved' ? (
											<Button type='button' variant='outline' size='sm' onClick={() => setReopening(entry)}>
												Reopen
											</Button>
										) : null}
									</li>
								))}
							</ul>
						) : (
							<p className={css(tt.muted)}>No hours in the last {RECENT_DAYS} days.</p>
						)}
						{selected ? (
							<div className={css(styles.history)}>
								<h3 className={css(styles.historyTitle)}>{formatDayShort(selected.day)} history</h3>
								<TimeEntryHistory entryId={selected.id} nameFor={nameFor} />
							</div>
						) : null}
					</div>
					<DrawerFooter>
						{canUseAdminActions ? (
							<Button type='button' variant='outline' onClick={() => setOpeningDay(true)}>
								Open a blocked day
							</Button>
						) : null}
						<Button type='button' variant='secondary' onClick={onClose}>
							Close
						</Button>
					</DrawerFooter>
				</DrawerContent>
			</Drawer>

			<ReasonDialog
				open={!!reopening}
				onOpenChange={open => !open && setReopening(null)}
				title={`Reopen ${repName}'s hours`}
				description={
					reopening ? `${formatDayShort(reopening.day)} · ${formatHours(reopening.hours)} hours, approved` : undefined
				}
				label='Reason'
				help='The day goes back to the rep as a new version. They see your reason in Coffee and by email.'
				confirmLabel='Reopen day'
				isPending={reopen.isPending}
				onConfirm={comment => reopening && reopen.mutate({ action: 'reopen', comment, id: reopening.id })}
			/>

			<ReasonDialog
				open={openingDay}
				onOpenChange={setOpeningDay}
				title={`Open a blocked day for ${repName}`}
				description='Weekends and holidays are blocked. Once opened, the rep can enter hours and they go through normal approval.'
				label='Reason'
				confirmLabel='Open day'
				canConfirm={isYmd(dayToOpen)}
				isPending={openBlocked.isPending}
				onConfirm={reason => userId && openBlocked.mutate({ day: dayToOpen, reason, userId })}
			>
				<Field>
					<FieldLabel>Day</FieldLabel>
					<DateInput value={dayToOpen} onChange={setDayToOpen} />
				</Field>
			</ReasonDialog>
		</>
	);
}

const styles = StyleSheet.create({
	body: {
		display: 'flex',
		flex: 1,
		flexDirection: 'column',
		gap: SPACE[16],
		overflowY: 'auto',
		padding: SPACE[16],
	},
	day: {
		alignItems: 'center',
		borderTop: `1px solid ${THEME.colors.border.light}`,
		display: 'flex',
		gap: SPACE[8],
		padding: `${SPACE[8]}px 0`,
	},
	dayButton: {
		alignItems: 'flex-start',
		flex: 1,
		flexDirection: 'column',
		gap: SPACE[4],
		height: 'auto',
		justifyContent: 'flex-start',
		padding: `${SPACE[4]}px ${SPACE[8]}px`,
		textAlign: 'left',
	},
	daySelected: {
		background: THEME.colors.primary.light,
	},
	dayTitle: {
		fontSize: THEME.typography.fontSizes.sm,
		fontWeight: THEME.typography.fontWeights.semibold,
	},
	days: {
		listStyle: 'none',
		margin: 0,
		padding: 0,
	},
	history: {
		display: 'flex',
		flexDirection: 'column',
		gap: SPACE[8],
	},
	historyTitle: {
		color: THEME.colors.text.main,
		fontSize: THEME.typography.fontSizes.xs,
		fontWeight: THEME.typography.fontWeights.semibold,
		margin: 0,
	},
	skeleton: {
		height: 200,
		width: '100%',
	},
});
